import React, { useState } from "react";
import { ArrowUpRight, Check, Loader2, Mail, Newspaper, Phone, Plus, Smartphone, Star, Zap } from "lucide-react";
import { LinkedEstablishment, NewsItem } from "../../interfaces/restaurantNews";
import { createProspect } from "../../interfaces/prospect";
import { invite } from "../../interfaces/linkedinProspecting";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { LinkedInLogo } from "../../components/icons/LinkedInLogo";
import TrialLockBanner from "../../components/TrialLockBanner";
import { TRIAL_LOCKED_CLASS } from "../../utils/trial";
import { formatCityWithLocation } from "../../lib/frenchCities";
import { ownerDisplayName } from "../../lib/personName";
import { NEWS_CATEGORY_LABEL, NEWS_CATEGORY_TONE, openingGap, newsTimeAgo } from "./newsMeta";
import { EnrichmentBoostModal } from "../../components/enrichment/EnrichmentBoostModal";

/**
 * Actualité CHR as a worklist: one row per article (signal, establishment + article, gérant,
 * time from creation to press, date) and, beside it, the selected article in full — the story,
 * the establishment sheet and the three things a rep does next: call, email, put it in the CRM.
 * Under `xl` the table needs the width, so the detail opens inline under its row instead.
 * Type sizes follow the app's other tables (SignalTable, LinkedIn): text-sm names, text-xs the rest.
 */

/**
 * Signal · établissement · gérant · création → presse · date. Phones keep the établissement and
 * the date (signal above the name, gérant under it); the creation gap needs `md`.
 */
const COLS =
    "grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[96px_minmax(0,1fr)_72px] md:grid-cols-[88px_minmax(0,1.3fr)_minmax(0,1fr)_168px] gap-x-4";

const ownerOf = (est: LinkedEstablishment | null) => (est?.owners && est.owners.length > 0 ? est.owners[0] : null);
const portfolioOf = (est: LinkedEstablishment | null) => {
    const mgr = ownerOf(est);
    return mgr ? mgr.activeEstablishments + mgr.closedEstablishments || 1 : 0;
};
/**
 * The establishment's opening — INSEE's creation date of this site (SIRET), stored as
 * `registration.creation_date` or, on older imports, `operations.opening_date`. With the article's
 * date it is all the timeline knows: nothing here speaks of the company (SIREN).
 */
const openingDateOf = (est: LinkedEstablishment | null) => est?.registration?.creation_date || est?.operations?.opening_date || null;
const founderHref = (name: string) => `/signals/founder?founderName=${encodeURIComponent(name)}`;
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

export function NewsBoard({
    items, selectedId, onSelect, visibleCount, isTrial, inCrm, onAddedToCrm, onEmail, footer, isAdmin = false, onEstablishmentUpdated,
}: {
    items: NewsItem[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    visibleCount: number;
    isTrial: boolean;
    /** Establishment ids already in the rep's CRM. */
    inCrm: Set<string>;
    onAddedToCrm: (establishmentId: string) => void;
    onEmail: (item: NewsItem) => void;
    /** Under the rows: load more / end of list. */
    footer?: React.ReactNode;
    /** Admins get the « Booster l'enrichissement » button on the detail. */
    isAdmin?: boolean;
    /** A boost refreshed this establishment's sheet. */
    onEstablishmentUpdated?: (est: LinkedEstablishment) => void;
}) {
    const selected = items.find((i) => i._id === selectedId) || null;
    const detail = (item: NewsItem) => (
        <NewsDetail
            key={item._id}
            item={item}
            inCrm={inCrm}
            onAddedToCrm={onAddedToCrm}
            onEmail={onEmail}
            isAdmin={isAdmin}
            onEstablishmentUpdated={onEstablishmentUpdated}
        />
    );

    return (
        <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
            <div className="overflow-hidden rounded-2xl border border-border bg-surface">
                <div className={`hidden sm:grid ${COLS} bg-surface-sunken px-4 py-2.5 font-sans text-xs font-semibold uppercase tracking-wide text-ink-faint`}>
                    <span>Signal</span>
                    <span>Établissement · article</span>
                    <span className="hidden md:block">Gérant</span>
                    <span className="md:hidden">Date</span>
                    <span className="hidden md:block" title="Quand l'article est paru (●), par rapport à l'ouverture de l'établissement (○)">
                        Parution
                    </span>
                </div>
                <ul aria-label="Actualités">
                    {items.map((item, index) => {
                        const locked = index >= visibleCount;
                        const on = item._id === selectedId;
                        return (
                            <React.Fragment key={item._id}>
                                {isTrial && index === visibleCount && (
                                    <li>
                                        <TrialLockBanner hiddenCount={items.length - visibleCount} className="border-x-0" />
                                    </li>
                                )}
                                <li className={`border-t border-border ${locked ? TRIAL_LOCKED_CLASS : ""}`}>
                                    <NewsRow item={item} on={on} onSelect={() => !locked && onSelect(item._id)} />
                                    {on && <div className="border-t border-border xl:hidden">{detail(item)}</div>}
                                </li>
                            </React.Fragment>
                        );
                    })}
                </ul>
                {footer}
            </div>

            <aside className="hidden overflow-hidden rounded-2xl border border-border bg-surface xl:sticky xl:top-4 xl:block" aria-label="Détail de l'actualité">
                {selected ? detail(selected) : <p className="px-6 py-16 text-center font-sans text-sm text-ink-faint">Sélectionnez une actualité</p>}
            </aside>
        </div>
    );
}

/** The gérant's name, with their LinkedIn profile one click away when we hold a validated one. */
function OwnerName({ est, className }: { est: LinkedEstablishment | null; className: string }) {
    const mgr = ownerOf(est);
    if (!mgr) return null;
    return (
        <span className={`flex min-w-0 items-center gap-1.5 ${className}`}>
            <span className="truncate" title={mgr.name}>
                {ownerDisplayName(mgr.name)}
            </span>
            {mgr.linkedinUrl && (
                <a
                    href={mgr.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={stop}
                    onKeyDown={stop}
                    title={mgr.linkedinInvite ? "Profil LinkedIn · déjà invité" : "Profil LinkedIn"}
                    aria-label="Profil LinkedIn"
                    className="relative shrink-0 text-[#0A66C2] transition-opacity hover:opacity-75"
                >
                    <LinkedInLogo className="h-3.5 w-3.5" />
                    {mgr.linkedinInvite && <span aria-hidden className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-success ring-2 ring-surface" />}
                </a>
            )}
        </span>
    );
}

/** The gérant's age, and under it their establishment count, opening their portfolio. */
function OwnerMeta({ est }: { est: LinkedEstablishment | null }) {
    const mgr = ownerOf(est);
    if (!mgr) return null;
    const portfolio = portfolioOf(est);
    return (
        <span className="block font-sans text-xs text-ink-muted">
            <span className="block">{mgr.age != null ? `${mgr.age} ans` : <span className="text-ink-faint">âge inconnu</span>}</span>
            <a
                href={founderHref(mgr.name)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={stop}
                onKeyDown={stop}
                title={`Voir les établissements de ${ownerDisplayName(mgr.name)}`}
                className="underline decoration-border-strong underline-offset-2 transition-colors hover:text-ink hover:decoration-ink"
            >
                {portfolio} établissement{portfolio > 1 ? "s" : ""}
            </a>
        </span>
    );
}

const isMobilePhone = (phone: string) => /^(06|07|\+336|\+337|\+3306|\+3307)/.test(phone.replace(/[\s.\-()]/g, ""));

/**
 * What we hold to reach this place, at a glance under the signal: mobile, landline, email,
 * LinkedIn — the establishment's and the gérant's together. Missing ones stay faded so the row
 * reads the same width every time. Indicators only: the actions live in the detail.
 */
function ContactIcons({ est }: { est: LinkedEstablishment | null }) {
    const mgr = ownerOf(est);
    const phones = [est?.phone, mgr?.phone].filter((p): p is string => !!p);
    const items = [
        { key: "mobile", on: phones.some(isMobilePhone), label: "Mobile", icon: <Smartphone className="h-3.5 w-3.5" /> },
        { key: "fixe", on: phones.some((p) => !isMobilePhone(p)), label: "Téléphone fixe", icon: <Phone className="h-3.5 w-3.5" /> },
        { key: "email", on: !!(est?.email || mgr?.email), label: "Email", icon: <Mail className="h-3.5 w-3.5" /> },
        { key: "linkedin", on: !!mgr?.linkedinUrl, label: "LinkedIn du gérant", icon: <LinkedInLogo className="h-3.5 w-3.5" /> },
    ];
    return (
        <span className="mt-1.5 flex items-center gap-1.5" aria-label="Coordonnées disponibles">
            {items.map((i) => (
                <span
                    key={i.key}
                    title={`${i.label} : ${i.on ? "disponible" : "non disponible"}`}
                    aria-label={`${i.label} ${i.on ? "disponible" : "non disponible"}`}
                    className={i.on ? (i.key === "linkedin" ? "text-[#0A66C2]" : "text-ink") : "text-ink-faint opacity-40"}
                >
                    {i.icon}
                </span>
            ))}
        </span>
    );
}

function NewsRow({ item, on, onSelect }: { item: NewsItem; on: boolean; onSelect: () => void }) {
    const est = item.linkedEstablishment;
    const mgr = ownerOf(est);
    const place = item.city ? formatCityWithLocation(item.city, item.departmentCode) : null;
    const gap = openingGap(openingDateOf(est), item.published);
    const badge = (
        <Badge tone={NEWS_CATEGORY_TONE[item.category]} className="!px-2 !py-0.5 !text-[11px]">
            {NEWS_CATEGORY_LABEL[item.category]}
        </Badge>
    );

    // A div, not a button: the row holds a link (the gérant's portfolio).
    return (
        <div
            role="button"
            tabIndex={0}
            aria-pressed={on}
            onClick={onSelect}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect();
                }
            }}
            className={`grid ${COLS} relative cursor-pointer px-4 py-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent ${
                on ? "bg-surface-sunken/60" : "hover:bg-surface-sunken/30"
            }`}
        >
            {on && <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-ink" />}
            <span className="hidden sm:block">
                {badge}
                {est && <ContactIcons est={est} />}
            </span>

            <span className="min-w-0">
                <span className="mb-1 block sm:hidden">
                    {badge}
                    {est && <ContactIcons est={est} />}
                </span>
                <span className="block truncate font-sans text-sm font-semibold text-ink">{est?.name || item.title}</span>
                {place && <span className="block truncate font-sans text-xs text-ink-muted">{place}</span>}
                {est && <span className="mt-0.5 block truncate font-sans text-xs text-ink">{item.title}</span>}
                {mgr && (
                    <span className="mt-1 block md:hidden">
                        <OwnerName est={est} className="font-sans text-xs font-medium text-ink" />
                        <OwnerMeta est={est} />
                    </span>
                )}
            </span>

            <span className="hidden min-w-0 md:block">
                {mgr ? (
                    <>
                        <OwnerName est={est} className="font-sans text-sm font-medium text-ink" />
                        <OwnerMeta est={est} />
                        {est?.phone && <span className="block truncate font-mono text-xs text-ink-muted">{est.phone}</span>}
                    </>
                ) : (
                    <span className="font-sans text-sm text-ink-faint">Non identifié</span>
                )}
            </span>

            <span className="text-right font-sans text-xs text-ink-muted sm:text-left md:hidden">{newsTimeAgo(item.published)}</span>
            <span className="hidden min-w-0 md:block">
                <ArticleTiming published={item.published} gap={gap} />
            </span>
        </div>
    );
}

/** Where the place stood when the article came out, read from the opening → article gap. */
function stageOf(days: number): { label: string; tone: string } {
    if (days < 0) return { label: "À venir", tone: "text-success" };
    if (days <= 100) return { label: "Lancement", tone: "text-success" };
    if (days <= 365) return { label: "1re année", tone: "text-warning" };
    return { label: "Établi", tone: "text-ink-muted" };
}

/** « 2 mois après », « 1 mois avant », « Même jour » — always said from the article's side. */
const gapWording = (gap: { days: number; label: string }) =>
    gap.days === 0 ? "Même jour" : `${gap.label} ${gap.days > 0 ? "après" : "avant"}`;

/**
 * The track spans two years on a square-root scale, so the first months — where openings sit —
 * get room; anything older sits at its left end.
 */
const TRACK_DAYS = 730;

/**
 * When the article came out, and where that falls in the establishment's life: the date and the
 * stage it puts the place in, a track with the opening (○) and the article (●) in time order —
 * dashed when the article came before the opening (an announced place) — then the gap in words.
 */
function ArticleTiming({ published, gap }: { published: string; gap: { days: number; label: string } | null }) {
    const when = <span className="text-sm font-medium text-ink">{newsTimeAgo(published)}</span>;
    if (!gap) {
        return (
            <span className="block font-sans">
                {when}
                <span className="block text-xs text-ink-faint">ouverture inconnue</span>
            </span>
        );
    }
    const stage = stageOf(gap.days);
    const ahead = gap.days < 0;
    // Distance between the two dots, kept visible however short the gap.
    const span = Math.max(14, Math.min(100, Math.sqrt(Math.abs(gap.days) / TRACK_DAYS) * 100));
    const dot = "absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full border-2 border-[currentColor]";

    return (
        <span className="block font-sans">
            <span className="flex items-baseline justify-between gap-2">
                {when}
                <span className={`text-xs font-semibold ${stage.tone}`}>{stage.label}</span>
            </span>
            <span className={`relative mt-1.5 block h-2.5 ${stage.tone}`}>
                <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border" />
                <span
                    className={`absolute top-1/2 ${ahead ? "border-t-2 border-dashed border-[currentColor]" : "h-0.5 -translate-y-1/2 bg-[currentColor]"}`}
                    style={{ right: 0, width: `${span}%`, ...(ahead ? { transform: "translateY(-50%)" } : {}) }}
                />
                {/* opening ○ — the later of the two dates sits at the right end */}
                <span className={`${dot} bg-surface`} style={{ right: ahead ? 0 : `calc(${span}% - 5px)` }} />
                {/* article ● */}
                <span className={`${dot} bg-[currentColor]`} style={{ right: ahead ? `calc(${span}% - 5px)` : 0 }} />
            </span>
            <span className="mt-1 block text-xs text-ink-muted">
                {gap.days === 0 ? "le jour de l'ouverture" : `${gapWording(gap).toLowerCase()} l'ouverture`}
            </span>
        </span>
    );
}

function NewsDetail({
    item, inCrm, onAddedToCrm, onEmail, isAdmin, onEstablishmentUpdated,
}: {
    item: NewsItem;
    inCrm: Set<string>;
    onAddedToCrm: (establishmentId: string) => void;
    onEmail: (item: NewsItem) => void;
    isAdmin?: boolean;
    onEstablishmentUpdated?: (est: LinkedEstablishment) => void;
}) {
    const est = item.linkedEstablishment;
    const mgr = ownerOf(est);
    const reg = est?.registration;
    const opened = openingDateOf(est);
    const gap = openingGap(opened, item.published);
    const [imageFailed, setImageFailed] = useState(false);
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState(false);
    const added = !!est && inCrm.has(est._id);
    const [liInvite, setLiInvite] = useState<"sent" | "queued" | null>(mgr?.linkedinInvite ?? null);
    const [liBusy, setLiBusy] = useState(false);
    const [boostOpen, setBoostOpen] = useState(false);

    /**
     * Same as « Se connecter » on the LinkedIn page: the profile opens (LinkedIn sends, never us)
     * and the invitation is recorded — in the rep's LinkedIn pipeline, quota counted, the gérant's
     * establishments pinned to « Décideurs ajoutés sur LinkedIn ». Past the daily quota it is queued.
     */
    const inviteOnLinkedin = async () => {
        if (!mgr?.linkedinUrl) return;
        window.open(mgr.linkedinUrl, "_blank", "noopener,noreferrer");
        setLiBusy(true);
        try {
            const res = await invite(mgr._id, "");
            setLiInvite(res.state.invite?.status === "queued" ? "queued" : "sent");
        } catch (e: any) {
            if (e?.response?.status === 409) setLiInvite("sent");
        } finally {
            setLiBusy(false);
        }
    };

    const addToCrm = async () => {
        if (!est) return;
        setAdding(true);
        setError(false);
        const name = mgr ? ownerDisplayName(mgr.name) : "";
        const [firstName, ...rest] = name.split(" ");
        const result = await createProspect({
            name: name || est.name,
            firstName: firstName || undefined,
            lastName: rest.join(" ") || undefined,
            company: est.name,
            email: est.email || undefined,
            phone: est.phone || undefined,
            website: est.website_url || undefined,
            linkedinUrl: mgr?.linkedinUrl || undefined,
            location: est.address?.city || item.city || undefined,
            establishmentId: est._id,
            source: "signal",
            status: "new",
        });
        setAdding(false);
        if (result && !result.error) onAddedToCrm(est._id);
        else setError(true);
    };

    const facts: Array<[string, React.ReactNode]> = est
        ? [
              [
                  "Établissement",
                  <a href={`/establishments/${est._id}`} target="_blank" rel="noopener noreferrer" className="font-medium hover:underline">
                      {est.name}
                  </a>,
              ],
              ["Activité", reg?.naf_description],
              ["Forme", reg?.legal_form],
              [
                  "Ouverture",
                  opened && (
                      <>
                          {new Date(opened).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                          {gap && (
                              <span className="text-ink-muted">
                                  {" · "}
                                  {gap.days === 0 ? "article le même jour" : `article ${gapWording(gap)}`}
                              </span>
                          )}
                      </>
                  ),
              ],
              [
                  "Note Google",
                  est.ratings?.google_avg != null && (
                      <span className="inline-flex items-center gap-1">
                          <Star className="h-3.5 w-3.5 fill-warning text-warning" />
                          {est.ratings.google_avg.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                          {est.ratings.google_count != null && <span className="text-ink-muted">· {est.ratings.google_count.toLocaleString("fr-FR")} avis</span>}
                      </span>
                  ),
              ],
              [
                  "Gérant",
                  mgr && (
                      <>
                          <OwnerName est={est} className="" />
                          <OwnerMeta est={est} />
                      </>
                  ),
              ],
              ["Téléphone", est.phone && <span className="font-mono text-xs">{est.phone}</span>],
              ["Email", est.email && <span className="break-all font-mono text-xs">{est.email}</span>],
          ]
        : [];

    return (
        <div>
            <a href={item.link} target="_blank" rel="noopener noreferrer" tabIndex={-1} className="block h-40 overflow-hidden bg-surface-sunken">
                {item.image && !imageFailed ? (
                    <img src={item.image} alt="" className="h-full w-full object-cover" onError={() => setImageFailed(true)} />
                ) : (
                    <span className="flex h-full w-full items-center justify-center bg-[repeating-linear-gradient(135deg,transparent_0_10px,rgba(0,0,0,0.035)_10px_20px)]">
                        <Newspaper className="h-5 w-5 text-ink-faint" />
                    </span>
                )}
            </a>

            <div className="p-5">
                <p className="flex min-w-0 items-center gap-1.5 font-sans text-xs text-ink-muted">
                    {item.source.favicon && (
                        <img
                            src={item.source.favicon}
                            alt=""
                            className="h-3 w-3 shrink-0 rounded-full"
                            onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                        />
                    )}
                    <span className="truncate">{item.source.domain || item.source.name}</span>
                    <span className="shrink-0">· {newsTimeAgo(item.published)}</span>
                </p>
                <h3 className="mt-1.5 font-sans text-base font-semibold leading-snug text-ink">{item.title}</h3>
                {item.description && <p className="mt-1.5 line-clamp-3 font-sans text-sm leading-relaxed text-ink-muted">{item.description}</p>}
                <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 font-sans text-sm font-medium text-ink underline underline-offset-4 hover:decoration-2"
                >
                    Lire l'article
                    <ArrowUpRight className="h-3.5 w-3.5" />
                </a>

                {est ? (
                    <>
                        <dl className="mt-4 grid grid-cols-[96px_minmax(0,1fr)] gap-x-3 gap-y-2 border-t border-border pt-4 font-sans text-sm">
                            {facts
                                .filter(([, value]) => value)
                                .map(([label, value]) => (
                                    <React.Fragment key={label}>
                                        <dt className="text-ink-muted">{label}</dt>
                                        <dd className="min-w-0 text-ink">{value}</dd>
                                    </React.Fragment>
                                ))}
                        </dl>

                        <div className="mt-5 grid grid-cols-2 gap-2 whitespace-nowrap sm:grid-cols-[1fr_1fr_auto]">
                            {est.phone ? (
                                <a
                                    href={`tel:${est.phone.replace(/\s/g, "")}`}
                                    className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border-strong font-sans text-sm font-medium text-ink transition-colors hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                >
                                    <Phone className="h-4 w-4" />
                                    Appeler
                                </a>
                            ) : (
                                <Button variant="secondary" size="sm" disabled title="Pas de numéro connu">
                                    <Phone className="h-4 w-4" />
                                    Appeler
                                </Button>
                            )}
                            <Button variant="secondary" size="sm" onClick={() => onEmail(item)} disabled={!est.email} title={est.email ? undefined : "Pas d'email connu"}>
                                <Mail className="h-4 w-4" />
                                Email
                            </Button>
                            {added ? (
                                <a
                                    href="/kanban"
                                    className="col-span-2 inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-success/30 bg-success-subtle px-3 font-sans text-sm font-medium text-success transition-colors hover:border-success/50 sm:col-span-1"
                                >
                                    <Check className="h-4 w-4" />
                                    Dans le CRM
                                </a>
                            ) : (
                                <Button size="sm" onClick={addToCrm} disabled={adding} className="col-span-2 sm:col-span-1">
                                    {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                                    Ajouter au CRM
                                </Button>
                            )}
                        </div>
                        {mgr?.linkedinUrl &&
                            (liInvite ? (
                                <a
                                    href="/prospection-linkedin"
                                    className="mt-2 flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border font-sans text-sm font-medium text-ink-muted transition-colors hover:text-ink"
                                >
                                    <Check className="h-4 w-4 text-success" />
                                    {liInvite === "queued" ? "Invitation LinkedIn en file (quota du jour atteint)" : "Invité sur LinkedIn · voir le suivi"}
                                </a>
                            ) : (
                                <button
                                    type="button"
                                    onClick={inviteOnLinkedin}
                                    disabled={liBusy}
                                    className="mt-2 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-[#0a66c2] font-sans text-sm font-semibold text-white transition-colors hover:bg-[#004182] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-60"
                                >
                                    {liBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkedInLogo className="h-4 w-4" />}
                                    Ajouter {ownerDisplayName(mgr.name).split(" ")[0]} sur LinkedIn
                                </button>
                            ))}
                        {error && <p className="mt-2 font-sans text-xs text-danger">L'ajout au CRM a échoué, réessayez.</p>}
                        {isAdmin && (
                            <button
                                type="button"
                                onClick={() => setBoostOpen(true)}
                                className="mt-2 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-danger font-sans text-sm font-semibold text-white transition-colors hover:bg-danger/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            >
                                <Zap className="h-4 w-4" />
                                Booster l'enrichissement
                            </button>
                        )}
                        {boostOpen && (
                            <EnrichmentBoostModal
                                establishment={est}
                                onClose={() => setBoostOpen(false)}
                                onUpdated={(next) => onEstablishmentUpdated?.(next)}
                            />
                        )}
                    </>
                ) : (
                    <p className="mt-4 border-t border-border pt-4 font-sans text-sm text-ink-muted">
                        Établissement pas encore identifié : la fiche, le gérant et ses coordonnées s'afficheront ici dès qu'il sera relié.
                    </p>
                )}
            </div>
        </div>
    );
}
